import React, { useState, useEffect } from "react";
import moment from "moment";
import { useLazyQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import PopupState, { bindMenu } from "material-ui-popup-state";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Checkbox from "@mui/material/Checkbox";
import CheckIcon from "@mui/icons-material/Check";
import Button from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OutboundBillingModal from "./orders/OutboundBillingModal";
import ActionProgressModal from "./ActionProgressModal";

import useNotification from "../../../hooks/notification";
import useMutation from "../../../hooks/useMutation";
import {
  DELETE_INVENTORY_ITEM_MUTATION,
  DELETE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
} from "../../../constants/graphql/mutations";
import { INVENTORY_REQUEST_VA_STATUS_DETAILED_QUERY } from "../../../constants/graphql/queries";
import useAuth from "src/hooks/useAuth";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import { statusColors, hoverColors } from "../../../constants/inventoryRequestStatus";
import { momentFormat } from "src/utils/dates";
import useMarkStatusAs from "./orders/hooks/useMarkStatusAs";
import useOrderData from "./hooks/useOrderData";
import { useNavigate } from "react-router";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";

// Priority order for status colors
export const statusOrder = [
  { key: "reserved", label: "Reserved" },
  { key: "canceled", label: "Cancelled" },
  { key: "completed", label: "Completed" },
  { key: "picked", label: "Picked" },
  { key: "change_order", label: "Change Order" },
  { key: "scheduled", label: "Scheduled" },
  { key: "pending_approval", label: "Pending Approval" },
  { key: "submitted", label: "Submitted" },
];

// Order for dropdown menu
const inventoryRequestStatusOrder = [
  { key: "pending_approval", label: "Pending Approval" },
  { key: "scheduled", label: "Scheduled" },
  { key: "picked", label: "Picked" },
  { key: "completed", label: "Completed" },
  { key: "canceled", label: "Cancelled" },
];

const internalWorkOrderStatusOrder = [
  { key: "picked", label: "Staged" },
  { key: "completed", label: "Completed" },
  { key: "canceled", label: "Cancelled" },
];

const statusOrders = {
  internalWorkOrder: internalWorkOrderStatusOrder,
  inventoryRequest: inventoryRequestStatusOrder,
};

export const styles = {
  menuPaper: {
    backgroundColor: "white",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
  },
  statusButton: selectedStatus => ({
    padding: "8px 12px",
    border: `1px solid ${statusColors[selectedStatus || "submitted"]}`,
    borderRadius: 1,
    backgroundColor: statusColors[selectedStatus || "submitted"],
    color: "black",
    textAlign: "center",
    boxSizing: "border-box",
    fontWeight: 500,
    fontSize: 16,
    lineHeight: 1.4,
  }),
  statusButtonHoverEffect: selectedStatus => ({
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: hoverColors[selectedStatus || "submitted"],
    },
  }),
  statusItem: itemDisabled => ({
    opacity: itemDisabled ? 0.5 : 1,
  }),
  statusHeader: {
    display: "flex",
    alignItems: "center",
    px: 2,
    py: 2,
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: 400,
    minHeight: 48,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  statusDetails: {
    pl: 6,
    pr: 2,
    pb: 2,
    fontSize: "1rem",
    color: "text.secondary",
    lineHeight: 1.5,
  },
  checkbox: {
    p: 0,
    mr: 1,
  },
  expandIcon: {
    ml: "auto",
    transition: "transform 0.2s ease",
  },
  expandIconRotated: {
    transform: "rotate(90deg)",
  },
};

const initialProgress = { show: false, current: 0, total: 0 };

export function getHighestPriorityStatus(vaStatus) {
  const found = statusOrder.find(s => vaStatus?.[s.key] === true);
  return found ? found.key : "";
}

export default function VaStatus({
  disabled,
  inventoryRequest,
  changeStatus,
  reFetch,
  setIsCancelling,
  orderType = "inventoryRequest",
  project,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [openOutboundBillingModal, setOpenOutboundBillingModal] = useState(false);
  const [pickingProgress, setPickingProgress] = useState({ show: false, current: 0, total: 0 });
  const [completionProgress, setCompletionProgress] = useState(initialProgress);
  const { notifySuccess } = useNotification();
  const [deleteInventoryItem] = useMutation(DELETE_INVENTORY_ITEM_MUTATION);
  const [deleteInternalWorkOrderItem] = useMutation(DELETE_INTERNAL_WORK_ORDER_ITEM_MUTATION);

  const { markAs, markAsCompleted } = useMarkStatusAs(orderType);
  const getOrderData = useOrderData(inventoryRequest.id, orderType);
  const isAdminOrManager = ["admin", "manager", "client"].includes(user.role);
  const isInventoryRole = user.role === "inventory";
  const isDisabled = disabled || (!isAdminOrManager && !isInventoryRole);
  const currentDropdownOrder = statusOrders[orderType];
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");
  const vaStatus = inventoryRequest?.vaStatus || {};

  // Get the appropriate label based on order type
  const getStatusLabel = statusKey => {
    const statusInDropdown = currentDropdownOrder.find(s => s.key === statusKey);
    if (statusInDropdown) return statusInDropdown.label;
    return statusOrder.find(s => s.key === statusKey)?.label;
  };

  useEffect(() => {
    setSelectedStatus(getHighestPriorityStatus(inventoryRequest?.vaStatus));
  }, [inventoryRequest]);

  // List/work-order queries no longer fetch vaStatusDetailed (it's expensive to
  // resolve per row). When the dropdown opens we lazily fetch the audit trail
  // for this single inventory request, unless it's already in context (e.g. the
  // detail page, whose query still includes it).
  const [fetchVaStatusDetailed, { data: lazyDetailedData }] = useLazyQuery(
    INVENTORY_REQUEST_VA_STATUS_DETAILED_QUERY,
    { fetchPolicy: "network-only" }
  );

  const loadVaStatusDetailed = () => {
    if (orderType !== "inventoryRequest") return;
    if (inventoryRequest?.vaStatusDetailed) return;
    if (!inventoryRequest?.id) return;

    fetchVaStatusDetailed({ variables: { id: inventoryRequest.id } });
  };

  const vaStatusDetailed =
    inventoryRequest?.vaStatusDetailed ||
    lazyDetailedData?.inventoryRequest?.vaStatusDetailed ||
    [];

  const detailedMap = vaStatusDetailed.reduce((acc, item) => {
    if (item && item.key) acc[item.key] = item;
    return acc;
  }, {});

  const toggleExpanded = key => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleCancel = async newValue => {
    setLoading(true);
    setIsCancelling?.(true);
    try {
      const { items } = await getOrderData();

      for (const item of items) {
        if (orderType === "internalWorkOrder") {
          await deleteInternalWorkOrderItem({ variables: { id: item.id } });
        } else {
          await deleteInventoryItem({ variables: { id: item.id } });
        }
      }

      await changeStatus("canceled")(newValue);

      if (reFetch) {
        const { data: refetchedData } = await reFetch();
        const newHighest = getHighestPriorityStatus(refetchedData.inventoryRequest.vaStatus);
        setSelectedStatus(newHighest);
      }

      notifySuccess("Order successfully cancelled");
    } catch (error) {
      console.error("Error during cancellation:", error);
    } finally {
      setLoading(false);
      setIsCancelling?.(false);
    }
  };

  const handleMarkAsPicked = async () => {
    const { items } = await getOrderData();
    const itemsToUpdate = items.filter(item => !item.pickedStatus);
    const totalItems = itemsToUpdate.length;

    if (totalItems > 0) {
      setPickingProgress({ show: true, current: 0, total: totalItems });

      for (let i = 0; i < itemsToUpdate.length; i++) {
        const item = itemsToUpdate[i];
        await markAs(item);
        setPickingProgress({ show: true, current: i + 1, total: totalItems });
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      setPickingProgress({ show: false, current: 0, total: 0 });
    }
  };

  const handleConfirmCancel = async () => {
    await handleCancel(true);
  };

  const handleMarkAsCompleted = async () => {
    if (orderType !== "internalWorkOrder") return;
    const { items } = await getOrderData();
    const itemsToUpdate = items.filter(item => !item.completedStatus);
    const totalItems = itemsToUpdate.length;
    if (totalItems > 0) {
      setCompletionProgress({ show: true, current: 0, total: totalItems });
      for (let i = 0; i < itemsToUpdate.length; i++) {
        const item = itemsToUpdate[i];
        await markAsCompleted(item);
        setCompletionProgress({ show: true, current: i + 1, total: totalItems });
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      setCompletionProgress({ show: false, current: 0, total: 0 });
    }
  };

  const handleChangeStatus = status => async () => {
    const newValue = !vaStatus[status];

    if (status === "canceled" && newValue) {
      setShowCancelModal(true);
    } else {
      setLoading(true);
      try {
        const isPickedOrCompleted = ["picked", "completed"].includes(status) && newValue;
        if (isPickedOrCompleted) {
          if (settings.manual_location_checkout) {
            await handleMarkAsPicked();
          }
          if (status === "completed" && orderType === "internalWorkOrder") {
            await handleMarkAsCompleted();
          }
          // COMMENTED OUT: Ancillary Charges modal temporarily disabled for backend work
          // setOpenOutboundBillingModal(true);
        }
        await changeStatus(status)(newValue);

        if (reFetch) {
          const { data: refetchedData } = await reFetch();
          const vaStatus =
            refetchedData?.inventoryRequest?.vaStatus || refetchedData?.workOrders?.vaStatus;
          const newHighest = getHighestPriorityStatus(vaStatus);
          setSelectedStatus(newHighest);
        }

        notifySuccess("Status updated successfully");

        if (
          newValue &&
          ["picked", "completed"].includes(status) &&
          orderType === "inventoryRequest" &&
          !(status === "completed" && inventoryRequest.vaStatus?.picked)
        ) {
          const params = new URLSearchParams(window.location.search);
          params.set("orderPicking", inventoryRequest.id);
          params.set("outboundOrderGuidedWizard", inventoryRequest.id);
          //const nextStep = hasAdditionalCharges ? "charges" : "confirmation";
          // params.set("step", nextStep);
          params.set("step", "items");
          navigate({ search: params.toString() });
        }
      } catch (err) {
        console.error("Error updating status:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <PopupState variant="popover" popupId="status-menu">
        {popupState => (
          <ClickAwayListener onClickAway={popupState.close}>
            <Box maxWidth={300} display="flex" flexDirection="column">
              <FormControl fullWidth>
                <Button
                  onClick={event => {
                    loadVaStatusDetailed();
                    popupState.open(event);
                  }}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                  sx={{
                    ...styles.statusButton(selectedStatus),
                    ...styles.statusButtonHoverEffect(selectedStatus),
                  }}
                >
                  {selectedStatus ? getStatusLabel(selectedStatus) : "Select Status"}
                </Button>
              </FormControl>

              <Menu
                {...bindMenu(popupState)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: styles.menuPaper,
                }}
              >
                {currentDropdownOrder.map(({ key, label }) => {
                  const isChecked = vaStatus[key] === true;
                  const isExpanded = expandedItems[key];
                  const detail = detailedMap[key] || {};
                  const hasDetailInfo = isChecked && (detail.updated_at || detail.updated_by);

                  let itemDisabled = isDisabled;

                  if (isInventoryRole) {
                    // For inventory users, disable all checkboxes except "completed"
                    // "completed" is only enabled if "picked" is already selected
                    if (key === "completed") {
                      itemDisabled = !vaStatus["picked"];
                    } else {
                      itemDisabled = true;
                    }
                  }

                  return (
                    <Box key={key} sx={styles.statusItem(itemDisabled)}>
                      <Box
                        sx={styles.statusHeader}
                        onClick={() => hasDetailInfo && toggleExpanded(key)}
                      >
                        {isChecked ? (
                          <CheckIcon
                            onClick={event => {
                              event.stopPropagation();
                              handleChangeStatus(key)();
                              popupState.close();
                            }}
                            sx={styles.checkbox}
                          />
                        ) : (
                          <Checkbox
                            checked={isChecked}
                            onChange={event => {
                              event.stopPropagation();
                              handleChangeStatus(key)();
                              popupState.close();
                            }}
                            disabled={itemDisabled}
                            sx={styles.checkbox}
                          />
                        )}
                        <Box>{label}</Box>
                        {hasDetailInfo &&
                          (isExpanded ? (
                            <ExpandMoreIcon sx={styles.expandIcon} />
                          ) : (
                            <ChevronRightIcon sx={styles.expandIcon} />
                          ))}
                      </Box>
                      {isChecked && isExpanded && hasDetailInfo && (
                        <Box sx={styles.statusDetails}>
                          <Box>Updated by: {detail.updated_by || "Unknown"}</Box>
                          <Box>
                            on{" "}
                            {detail.updated_at
                              ? moment(detail.updated_at).format(momentFormat)
                              : "Unknown"}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Menu>
            </Box>
          </ClickAwayListener>
        )}
      </PopupState>
      {showCancelModal && (
        <ConfirmDeleteModal
          title="Cancel Order"
          descr="Are you sure you want to cancel this order?"
          onClose={() => setShowCancelModal(false)}
          onDelete={handleConfirmCancel}
          applyLabel="Cancel Order"
        />
      )}
      {openOutboundBillingModal && (
        <OutboundBillingModal
          open={openOutboundBillingModal}
          inventoryRequest={inventoryRequest}
          onClose={() => setOpenOutboundBillingModal(false)}
        />
      )}
      {pickingProgress.show && (
        <ActionProgressModal
          open={pickingProgress.show}
          progress={pickingProgress.current}
          total={pickingProgress.total}
          title="Picking in Progress"
          actionLabel="picked"
        />
      )}
      {completionProgress.show && (
        <ActionProgressModal
          open={completionProgress.show}
          progress={completionProgress.current}
          total={completionProgress.total}
          title="Completing in Progress"
          actionLabel="completed"
        />
      )}
    </>
  );
}
